import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

export interface ChartTheme {
  tickColor: string;
  gridColorX: string;
  gridColorY: string;
  tooltipBg: string;
  tooltipTitle: string;
  tooltipBody: string;
  tooltipBorder: string;
  legendColor: string;
  pointBorder: string;
  donutBorder: string;
}

interface ThemeCtx {
  isDark: boolean;
  toggle: () => void;
  chart: ChartTheme;
}

const DARK: ChartTheme = {
  tickColor: '#64748b',
  gridColorX: 'rgba(255,255,255,0.03)',
  gridColorY: 'rgba(255,255,255,0.04)',
  tooltipBg: '#1a1f35',
  tooltipTitle: '#f1f5f9',
  tooltipBody: '#cbd5e1',
  tooltipBorder: 'rgba(255,255,255,0.1)',
  legendColor: '#94a3b8',
  pointBorder: '#0a0e1a',
  donutBorder: '#1a1f35',
};

const LIGHT: ChartTheme = {
  tickColor: '#A0AEC0',
  gridColorX: 'rgba(0,0,0,0.05)',
  gridColorY: 'rgba(0,0,0,0.06)',
  tooltipBg: '#FFFFFF',
  tooltipTitle: '#2D3436',
  tooltipBody: '#4A5568',
  tooltipBorder: 'rgba(0,0,0,0.10)',
  legendColor: '#4A5568',
  pointBorder: '#FFFFFF',
  donutBorder: '#FFFFFF',
};

const Ctx = createContext<ThemeCtx>({ isDark: true, toggle: () => {}, chart: DARK });

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDark, setIsDark] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('jgjb-theme');
      return saved !== null ? saved === 'dark' : true;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    document.documentElement.classList.toggle('light-mode', !isDark);
    localStorage.setItem('jgjb-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  const value = useMemo<ThemeCtx>(
    () => ({ isDark, toggle: () => setIsDark(d => !d), chart: isDark ? DARK : LIGHT }),
    [isDark]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useTheme = () => useContext(Ctx);
